import React, { useState, useEffect, useRef } from 'react'
import { Avatar, Box, CircularProgress, IconButton, InputAdornment, Stack, TextField, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Grid } from '@mui/material'
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LaunchIcon from '@mui/icons-material/Launch';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useDispatch, useSelector } from 'react-redux';
import { selectWhatsappState } from '../../state/whatsapp/whatsappSelector';
import { sendMessage, getUserMessages, sendGenericTemplateMessage, getTemplates, getUserMessagesFrequent, getTemplateStatus, clearTemplateStatus } from '../../state/whatsapp/whatsappSlice';
import Styles from './whatsapp.module.scss'
import WhatsappTemplateModal from './WhatsappTemplateModal';
import { formatBody, formatWhatsappPhone, getPreviewBody } from '../../helpers/utils/textFormatter';

const WhatsappChatSection = ({ selectedContact }) => {
    const dispatch = useDispatch();
    const { messages, isMessagesLoading, isMessagesSuccess, isNewArrived, isSending, templates } = useSelector(selectWhatsappState);
    const [inputMessage, setInputMessage] = useState('');
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const chatEndRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const inactivityTimeoutRef = useRef(null);
    const resumePollingTimeoutRef = useRef(null);
    const isPollingPausedRef = useRef(false);
    const previousIsSendingRef = useRef(isSending);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [isMessagesSuccess, isNewArrived]);

    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    const startPolling = (phone) => {
        if (!phone || pollingIntervalRef.current || isPollingPausedRef.current) return;

        dispatch(getUserMessagesFrequent({ phone, page: 1, perPage: 1000 }));
        pollingIntervalRef.current = setInterval(() => {
            dispatch(getUserMessagesFrequent({ phone, page: 1, perPage: 1000 }));
        }, 5000);
    };

    const clearInactivityTimer = () => {
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
            inactivityTimeoutRef.current = null;
        }
    };

    const resetInactivityTimer = (phone) => {
        if (!phone || isPollingPausedRef.current) return;

        startPolling(phone);
        clearInactivityTimer();
        inactivityTimeoutRef.current = setTimeout(() => {
            stopPolling();
        }, 1000);
    };

    useEffect(() => {
        if (!selectedContact) return;

        const phone = selectedContact.phone.replace('whatsapp:', '').replace('+', '');
        isPollingPausedRef.current = false;
        clearTimeout(resumePollingTimeoutRef.current);
        resumePollingTimeoutRef.current = null;

        const handleActivity = () => resetInactivityTimer(phone);

        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keydown', handleActivity);

        resetInactivityTimer(phone);

        return () => {
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleActivity);
            stopPolling();
            clearInactivityTimer();
            clearTimeout(resumePollingTimeoutRef.current);
            resumePollingTimeoutRef.current = null;
        };
    }, [selectedContact, dispatch]);

    useEffect(() => {
        if (!selectedContact) return;

        const phone = selectedContact.phone.replace('whatsapp:', '').replace('+', '');
        const wasSending = previousIsSendingRef.current;

        if (isSending && !wasSending) {
            isPollingPausedRef.current = true;
            clearTimeout(resumePollingTimeoutRef.current);
            resumePollingTimeoutRef.current = null;
            stopPolling();
            clearInactivityTimer();
        }

        if (!isSending && wasSending) {
            clearTimeout(resumePollingTimeoutRef.current);
            resumePollingTimeoutRef.current = setTimeout(() => {
                isPollingPausedRef.current = false;
                resetInactivityTimer(phone);
            }, 5000);
        }

        previousIsSendingRef.current = isSending;
    }, [isSending, selectedContact]);

    const handleSendMessage = () => {
        if (!inputMessage.trim() || isSending) return;

        const toPhone = selectedContact.phone.replace('whatsapp:', '');
        dispatch(sendMessage({ to: toPhone, body: inputMessage.trim() }));
        setInputMessage('');
        // Refetch messages after a short delay to show the sent message
        // setTimeout(() => {
        //     dispatch(getUserMessages({ phone, page: 1, perPage: 1000 }));
        // }, 1500);
    };

    const handleSendTemplateMessage = (templateVariables, selectedTemplateId, phoneNumber) => {
        const activeTemplate = templates.find(t => t.sid === selectedTemplateId);
        if (!activeTemplate) return;
        const toPhone = phoneNumber.replace('whatsapp:', '');
        const formattedVars = {};
        const varKeys = activeTemplate.variables && Object.keys(activeTemplate.variables).length > 0
            ? Object.keys(activeTemplate.variables).sort((a, b) => Number(a) - Number(b))
            : getVariablesFromBody(activeTemplate.body);

        varKeys.forEach(k => {
            formattedVars[k] = templateVariables[k] || "";
        });

        const bodyPreview = getPreviewBody(activeTemplate.body, templateVariables);
        dispatch(sendGenericTemplateMessage({
            to: toPhone,
            templateSid: activeTemplate.sid,
            variables: formattedVars,
            body: bodyPreview
        }));

        setIsTemplateModalOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();

        const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        if (isToday) {
            return timeStr;
        }
        const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
        return `${dateStr}, ${timeStr}`;
    };

    if (!selectedContact) {
        return (
            <Box className={Styles.noChatSelected}>
                <Stack alignItems="center" spacing={1}>
                    <WhatsAppIcon sx={{ fontSize: 64, color: '#25D366', opacity: 0.5 }} />
                    <Typography variant="h6" color="text.secondary">
                        Select a conversation to start chatting
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                        Choose a contact from the sidebar
                    </Typography>
                </Stack>
            </Box>
        );
    }

    const inboundMessages = messages.filter(m => m.direction === 'inbound');
    const latestInbound = inboundMessages.length > 0 ? inboundMessages[inboundMessages.length - 1] : null;

    let sessionExpired = true;
    if (latestInbound) {
        const hours = (new Date() - new Date(latestInbound.dateCreated)) / (1000 * 60 * 60);
        if (hours < 23) {
            sessionExpired = false;
        }
    }

    return (
        <Box className={Styles.chatSection}>
            {/* Chat Header */}
            <Box sx={{ p: 1.5, px: 2, backgroundColor: '#f0f2f5', borderBottom: '1px solid #e0e0e0' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: '#25D366', color: '#fff', width: 36, height: 36 }} />
                    <Typography variant="subtitle2">{formatWhatsappPhone(selectedContact.phone)}</Typography>
                </Stack>
            </Box>

            {/* Chat Messages */}
            <Box className={Styles.chatMessages}>
                {isMessagesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Stack direction="column">
                        {messages.map((message) => (
                            <Box
                                key={message.sid}
                                className={`${Styles.messageBubble} ${message.direction === 'inbound' ? Styles.messageReceived : Styles.messageSent}`}
                            >
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                    {formatBody(message.body)}
                                </Typography>
                                <Typography className={Styles.messageTime}>
                                    {formatTime(message.dateCreated)}
                                </Typography>
                                {(message.status === 'failed' || message.status === 'undelivered') && (
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                        <ErrorOutlineIcon sx={{ fontSize: 13, color: '#d32f2f' }} />
                                        <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: 11 }}>
                                            Failed to deliver
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>
                        ))}
                        <div ref={chatEndRef} />
                    </Stack>
                )}
            </Box>

            {/* Chat Input */}
            <Box className={Styles.chatInputArea}>
                {sessionExpired ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            fullWidth
                            disabled
                            multiline
                            minRows={1}
                            maxRows={1}
                            size="small"
                            value="Service messaging window closed. Please send a template message."
                            InputProps={{
                                sx: { backgroundColor: '#f5f5f5', borderRadius: '8px' },
                            }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<LaunchIcon />}
                            onClick={() => setIsTemplateModalOpen(true)}
                            sx={{ whiteSpace: 'nowrap', bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
                        >
                            Open Templates
                        </Button>
                    </Stack>
                ) : (
                    <TextField
                        fullWidth
                        multiline
                        maxRows={8}
                        minRows={3}
                        size="small"
                        placeholder="Type a message..."
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleSendMessage}
                                        disabled={!inputMessage.trim() || isSending}
                                        sx={{ color: '#25D366' }}
                                    >
                                        {isSending ? <CircularProgress size={20} /> : <SendIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: { backgroundColor: '#fff', borderRadius: '8px' },
                        }}
                    />
                )}
            </Box>

            {/* Template Selection Dialog */}
            <WhatsappTemplateModal
                isLoading={isSending}
                open={isTemplateModalOpen}
                selectedContact={selectedContact}
                onClose={() => setIsTemplateModalOpen(false)}
                onSubmit={handleSendTemplateMessage}
            />
        </Box>
    );
};

export default WhatsappChatSection;
